import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  CircularProgress,
  Box,
} from '@mui/material';
import { Info as InfoIcon } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { announcementsAPI, SystemAnnouncement } from '../services/api';

const SystemAnnouncementPopup: React.FC = () => {
  const { user } = useAuth();
  const [announcement, setAnnouncement] = useState<SystemAnnouncement | null>(null);
  const [loading, setLoading] = useState(false);
  const [acknowledging, setAcknowledging] = useState(false);

  useEffect(() => {
    if (!user) {
      setAnnouncement(null);
      return;
    }

    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const active = await announcementsAPI.getActive();
        if (!cancelled) {
          setAnnouncement(active);
        }
      } catch {
        if (!cancelled) {
          setAnnouncement(null);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [user?.id]);

  const handleAcknowledge = async () => {
    if (!announcement) return;
    setAcknowledging(true);
    try {
      await announcementsAPI.acknowledge(announcement.id);
      setAnnouncement(null);
    } catch {
      // Lascia il popup aperto in caso di errore
    } finally {
      setAcknowledging(false);
    }
  };

  if (loading || !announcement) {
    return null;
  }

  return (
    <Dialog
      open
      disableEscapeKeyDown
      onClose={() => {
        // Solo presa visione chiude il popup
      }}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <InfoIcon color="info" />
        {announcement.title}
      </DialogTitle>
      <DialogContent dividers>
        <Typography
          variant="body1"
          sx={{ whiteSpace: 'pre-wrap', mb: 2 }}
        >
          {announcement.message}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Cliccando su &quot;Ho preso visione&quot; dichiari di aver letto questo messaggio.
          Non ti verrà più mostrato.
        </Typography>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Box sx={{ flexGrow: 1 }} />
        <Button
          variant="contained"
          onClick={handleAcknowledge}
          disabled={acknowledging}
          startIcon={acknowledging ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          Ho preso visione
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SystemAnnouncementPopup;
